import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ConfigsService } from '@overa/shared';

/** Temas que genera `styles/themes/_generator.scss`. El del BackOffice es `theme-admin`. */
export type ThemeName = 'theme-admin' | 'theme-app' | 'theme-software' | 'theme-default';

const THEMES: ThemeName[] = ['theme-admin', 'theme-app', 'theme-software', 'theme-default'];

const FALLBACK_THEME: ThemeName = 'theme-admin';

/**
 * Aplica la clase de tema al documento.
 *
 * ## Por qué hacía falta
 *
 * `app.config.ts` declaraba `overa-shared.defaultTheme = 'theme-admin'` y **nadie leía ese valor**: ni la
 * UILib ni el BackOffice ponían la clase en ninguna parte, y `<body>` salía sin clase. Consecuencia: todo
 * el preset `admin` (`styles/themes/presets/admin/_tokens.scss`) era código muerto y lo que aplicaba era
 * el `:root` de `_generator.scss`, que emite el preset **app**. Activity sí tiene un servicio así — de
 * hecho este es su equivalente reducido— y por eso allí los temas funcionan.
 *
 * El síntoma por el que se descubrió: en `overa-datatable` no se veía bien el color de la fila
 * seleccionada. Y no era casualidad que apareciese ahora — las listas de las pantallas de seguridad son
 * las primeras tablas de la UILib que se usan en el BackOffice.
 *
 * ## Por qué la clase va en `<html>` Y en `<body>`
 *
 * No es por redundancia: es lo que hace que el tema gane. `styles.scss` importa los ficheros de
 * `styles/uilib/*` **después** del tema, y esos declaran los `--o-*` en `:root` mapeándolos desde
 * `--default-*`; por ejemplo `--o-datatable-row-selected-background: var(--default-table-row-selected-background)`.
 * Un selector de clase y `:root` tienen la misma especificidad (0,1,0), así que a igualdad gana el que va
 * después — el de `styles/uilib/`— y los valores del preset quedaban tapados.
 *
 * Poniendo además la clase en `<body>`, el valor del preset se declara en un elemento **más cercano** a
 * la tabla, y para una custom property eso vence a lo que se heredaría de `:root`. Es el mismo truco que
 * usa Activity, y sin él aplicar el tema no arreglaría los colores de la tabla.
 *
 * Si algún día se quiere quitar el truco, lo correcto es que `_generator.scss` emita
 * `:root.theme-admin` (0,2,0) en lugar de `.theme-admin`, o que los ficheros de `styles/uilib/` se
 * importen antes del tema.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private currentTheme: ThemeName = FALLBACK_THEME;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly configService: ConfigsService,
  ) {
    this.applyTheme(this.configuredTheme());
  }

  get theme(): ThemeName {
    return this.currentTheme;
  }

  applyTheme(theme: ThemeName): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const html = document.documentElement;
    const body = document.body;

    // Se limpian los demás para que no queden tokens de un tema anterior sueltos en el árbol.
    for (const previous of THEMES) {
      html.classList.remove(previous);
      body.classList.remove(previous);
    }

    html.classList.add(theme);
    body.classList.add(theme);

    this.currentTheme = theme;

    // El login de la UILib (`AuthService.getCurrentThemeForIdp`) lee esta clave y la manda al IdP como
    // `theme=...` en la petición de autorización; sin ella las pantallas del IdP salen con el tema `default`.
    // Va sin el prefijo (`admin`): el IdP busca `assets/themes/{tema}/global.css`.
    try {
      localStorage.setItem('current-theme', theme.replace('theme-', ''));
    } catch {
      // Sin localStorage (modo privado estricto) el IdP cae al tema `default`; no debe romper el arranque.
    }
  }

  /**
   * Tema declarado en `app.config.ts`.
   *
   * Con valor por defecto en `getModuleConfig`: sin él, ese método **lanza** cuando el campo no existe, y
   * un tema mal configurado no debe impedir que la aplicación arranque.
   */
  private configuredTheme(): ThemeName {
    const configured = this.configService.getModuleConfig('overa-shared', 'defaultTheme', FALLBACK_THEME);

    return THEMES.includes(configured as ThemeName) ? (configured as ThemeName) : FALLBACK_THEME;
  }
}
